/**
 * Stripe Analytics Page
 *
 * Payment and subscription analytics including MRR, ARR,
 * churn analysis, failed payments, and tier distribution.
 */

import { useState, useEffect } from 'react';
import { thirdPartyAnalyticsAPI } from '../services/api';
import { useEnvironment } from '../context/EnvironmentContext';
import PageHeader from '../components/analytics/PageHeader';
import MetricCard from '../components/analytics/MetricCard';
import TrendChart from '../components/founder-dashboard/TrendChart';
import DataTable from '../components/founder-dashboard/DataTable';
import DistributionChart from '../components/founder-dashboard/DistributionChart';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  UserGroupIcon,
  BeakerIcon,
  ExclamationTriangleIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

const TIER_COLORS = {
  free: '#9CA3AF',
  starter: '#3B82F6',
  pro: '#8B5CF6',
  enterprise: '#F59E0B',
};

export default function StripePage() {
  const { currentEnv } = useEnvironment();
  const [overview, setOverview] = useState(null);
  const [mrrByTier, setMrrByTier] = useState(null);
  const [failedPayments, setFailedPayments] = useState(null);
  const [churnAnalysis, setChurnAnalysis] = useState(null);
  const [revenueAtRisk, setRevenueAtRisk] = useState(null);
  const [tierChanges, setTierChanges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, mrrRes, failedRes, churnRes, riskRes, changesRes] = await Promise.all([
        thirdPartyAnalyticsAPI.getStripeOverview(),
        thirdPartyAnalyticsAPI.getStripeMrrByTier(),
        thirdPartyAnalyticsAPI.getStripeFailedPayments(),
        thirdPartyAnalyticsAPI.getStripeChurnAnalysis(),
        thirdPartyAnalyticsAPI.getStripeRevenueAtRisk(),
        thirdPartyAnalyticsAPI.getStripeTierChanges(),
      ]);
      setOverview(overviewRes.data);
      setMrrByTier(mrrRes.data);
      setFailedPayments(failedRes.data);
      setChurnAnalysis(churnRes.data);
      setRevenueAtRisk(riskRes.data);
      setTierChanges(changesRes.data);
    } catch (err) {
      console.error('[StripePage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentEnv]);

  // Status indicators
  const getFailedPaymentsStatus = () => {
    const count = overview?.failedPayments24h || 0;
    if (count > 5) return 'critical';
    if (count > 2) return 'warning';
    return 'healthy';
  };

  const getChurnStatus = () => {
    const rate = churnAnalysis?.monthlyChurnRate || 0;
    if (rate > 10) return 'critical';
    if (rate > 5) return 'warning';
    return 'healthy';
  };

  if (error) {
    return (
      <div className="p-6">
        <PageHeader title="Stripe Analytics" />
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading Stripe analytics</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Prepare MRR distribution data
  const mrrDistribution = {};
  (mrrByTier?.tiers || []).forEach((t) => {
    mrrDistribution[t.tierName || t.tierCode] = t.mrr;
  });

  // Failed payments table columns
  const failedColumns = [
    { key: 'organization_name', header: 'Organization', sortable: true },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (val) => `$${val?.toFixed(2) || '0.00'}`,
    },
    { key: 'failure_reason', header: 'Reason', sortable: false },
    { key: 'attempts', header: 'Attempts', sortable: true },
    {
      key: 'next_retry',
      header: 'Next Retry',
      sortable: true,
      render: (val) => val ? new Date(val).toLocaleDateString() : 'N/A',
    },
  ];

  // Tier changes table columns
  const tierChangeColumns = [
    { key: 'organization_name', header: 'Organization', sortable: true },
    { key: 'from_tier', header: 'From', sortable: true },
    { key: 'to_tier', header: 'To', sortable: true },
    {
      key: 'mrr_change',
      header: 'MRR Change',
      sortable: true,
      render: (val) => {
        const isPositive = val >= 0;
        return (
          <span className={isPositive ? 'text-green-600' : 'text-red-600'}>
            {isPositive ? '+' : ''}${val?.toFixed(2) || '0.00'}
          </span>
        );
      },
    },
    {
      key: 'changed_at',
      header: 'Date',
      sortable: true,
      render: (val) => new Date(val).toLocaleDateString(),
    },
  ];

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <PageHeader
        title="Stripe Analytics"
        description="Payment processing and subscription metrics"
        onRefresh={fetchData}
        loading={loading}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Monthly Recurring Revenue"
          value={overview?.mrr}
          format="currency"
          icon={CurrencyDollarIcon}
          change={overview?.mrrChange}
          loading={loading}
        />
        <MetricCard
          title="Annual Recurring Revenue"
          value={overview?.arr}
          format="currency"
          icon={ChartBarIcon}
          loading={loading}
        />
        <MetricCard
          title="Active Subscriptions"
          value={overview?.activeSubscriptions}
          icon={UserGroupIcon}
          loading={loading}
        />
        <MetricCard
          title="Active Trials"
          value={overview?.trialsActive}
          icon={BeakerIcon}
          subtitle={`${overview?.trialConversionRate?.toFixed(1) || 0}% conversion`}
          loading={loading}
        />
      </div>

      {/* Revenue by Tier */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* MRR by Tier Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Revenue by Tier
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={(mrrByTier?.tiers || []).map(t => ({
                  name: t.tierName || t.tierCode,
                  mrr: t.mrr,
                  tierCode: t.tierCode,
                }))}
                margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={{ stroke: '#E5E7EB' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={{ stroke: '#E5E7EB' }}
                  tickFormatter={(val) => `$${val}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  formatter={(val) => [`$${val.toFixed(2)}`, 'MRR']}
                />
                <Bar dataKey="mrr" radius={[4, 4, 0, 0]}>
                  {(mrrByTier?.tiers || []).map((tier, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={TIER_COLORS[tier.tierCode] || '#3B82F6'}
                    />
                  ))}
                  <LabelList
                    dataKey="mrr"
                    position="top"
                    formatter={(val) => `$${val.toFixed(0)}`}
                    style={{ fontSize: 11, fill: '#374151', fontWeight: 500 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Subscribers by Tier Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Subscribers by Tier
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={(mrrByTier?.tiers || []).map(t => ({
                  name: t.tierName || t.tierCode,
                  subscribers: t.subscriptionCount,
                  tierCode: t.tierCode,
                }))}
                margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={{ stroke: '#E5E7EB' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={{ stroke: '#E5E7EB' }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Bar dataKey="subscribers" radius={[4, 4, 0, 0]}>
                  {(mrrByTier?.tiers || []).map((tier, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={TIER_COLORS[tier.tierCode] || '#3B82F6'}
                    />
                  ))}
                  <LabelList
                    dataKey="subscribers"
                    position="top"
                    style={{ fontSize: 11, fill: '#374151', fontWeight: 500 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* MRR Distribution Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            MRR Distribution
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <DistributionChart
              data={mrrDistribution}
              height={200}
              showPercentage
            />
          )}
        </div>
      </div>

      {/* Failed Payments */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Failed Payments
          </h3>
          <div className="flex items-center gap-4 text-sm">
            <span className={`px-3 py-1 rounded-full ${
              getFailedPaymentsStatus() === 'critical' ? 'bg-red-100 text-red-800' :
              getFailedPaymentsStatus() === 'warning' ? 'bg-yellow-100 text-yellow-800' :
              'bg-green-100 text-green-800'
            }`}>
              24h: {overview?.failedPayments24h || 0}
            </span>
            <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full">
              7d: {overview?.failedPayments7d || 0}
            </span>
            <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full">
              30d: {overview?.failedPayments30d || 0}
            </span>
            {revenueAtRisk?.total > 0 && (
              <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full font-medium">
                At Risk: ${revenueAtRisk?.total?.toFixed(2)}
              </span>
            )}
          </div>
        </div>
        <DataTable
          data={failedPayments?.payments || []}
          columns={failedColumns}
          loading={loading}
          emptyMessage="No failed payments"
        />
      </div>

      {/* Churn Analysis + Tier Changes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Churn Analysis */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Churn Analysis
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Monthly Churn Rate</p>
                  <p className={`text-2xl font-bold ${
                    getChurnStatus() === 'critical' ? 'text-red-600' :
                    getChurnStatus() === 'warning' ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                    {churnAnalysis?.monthlyChurnRate?.toFixed(1) || 0}%
                  </p>
                </div>
                <ArrowTrendingDownIcon className={`w-8 h-8 ${
                  getChurnStatus() === 'critical' ? 'text-red-400' :
                  getChurnStatus() === 'warning' ? 'text-yellow-400' :
                  'text-green-400'
                }`} />
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Average Customer Tenure</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {churnAnalysis?.avgTenureMonths?.toFixed(1) || 0} months
                  </p>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-2">Churn by Tier</p>
                {(churnAnalysis?.byTier || []).map((tier) => (
                  <div key={tier.tier} className="flex items-center justify-between py-1">
                    <span className="capitalize">{tier.tier}</span>
                    <span className="font-medium">{tier.churnRate?.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tier Changes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Tier Changes (30d)
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 p-4 bg-green-50 rounded-lg border border-green-100">
                  <div className="flex items-center gap-2">
                    <ArrowTrendingUpIcon className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-800">Upgrades</span>
                  </div>
                  <p className="text-2xl font-bold text-green-700 mt-1">
                    {tierChanges?.upgrades?.count || 0}
                  </p>
                  <p className="text-sm text-green-600">
                    +${(tierChanges?.upgrades?.mrrGained || 0).toFixed(2)}
                  </p>
                </div>
                <div className="flex-1 p-4 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-center gap-2">
                    <ArrowTrendingDownIcon className="w-5 h-5 text-red-600" />
                    <span className="text-sm text-red-800">Downgrades</span>
                  </div>
                  <p className="text-2xl font-bold text-red-700 mt-1">
                    {tierChanges?.downgrades?.count || 0}
                  </p>
                  <p className="text-sm text-red-600">
                    -${(tierChanges?.downgrades?.mrrLost || 0).toFixed(2)}
                  </p>
                </div>
              </div>
              <DataTable
                data={tierChanges?.recent || []}
                columns={tierChangeColumns}
                loading={loading}
                emptyMessage="No tier changes"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
